import { FC, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ContactContent } from '../services/contentService';

interface ContactProps {
  content?: ContactContent;
}

const Contact: FC<ContactProps> = ({ content }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Form state
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default content if CMS data is not available
  const defaultContent: ContactContent = {
    title: "Kontakt",
    phone: "+48 600 375 251",
    phone2: "+48 692 417 607",
    email: "aluxim@aluxim.pl",
    address: "Siedziba: Ul. Gorzowska 54, 64-980 Trzcianka",
    workshop: "Warsztat: Ul. Popiełuszki 11, 64-980 Trzcianka"
  };

  const contactContent = content || defaultContent;

  const contactItems = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: "Adresy",
      content: (
        <div>
          <div className="mb-1">{contactContent.address}</div>
          {contactContent.workshop && <div>{contactContent.workshop}</div>}
        </div>
      )
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: "Telefony",
      content: (
        <div>
          <div className="mb-1">
            <a href={`tel:${contactContent.phone}`} className="hover:text-blue-700 transition-colors">
              {contactContent.phone}
            </a>
          </div>
          {contactContent.phone2 && (
            <div>
              <a href={`tel:${contactContent.phone2}`} className="hover:text-blue-700 transition-colors">
                {contactContent.phone2}
              </a>
            </div>
          )}
        </div>
      )
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: "E-mail",
      content: (
        <a href={`mailto:${contactContent.email}`} className="hover:text-blue-700 transition-colors">
          {contactContent.email}
        </a>
      )
    }
  ];

  const formFields = [
    { id: "name", label: "Imię i nazwisko", type: "text", colSpan: "md:col-span-1" },
    { id: "email", label: "E-mail", type: "email", colSpan: "md:col-span-1" },
    { id: "subject", label: "Temat", type: "text", colSpan: "col-span-2" },
    { id: "message", label: "Wiadomość", type: "textarea", colSpan: "col-span-2", rows: 5 }
  ];

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  // Handle form submit - open mailto link with filled data
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSent(false);

    if (!form.name || !form.email || !form.subject || !form.message) {
      setError('Wypełnij wszystkie pola.');
      return;
    }

    const mailto = [
      `mailto:aluxim@aluxim.pl`,
      `?subject=${encodeURIComponent(form.subject)}`,
      `&body=${encodeURIComponent(
        `Imię i nazwisko: ${form.name}\nE-mail: ${form.email}\n\n${form.message}`
      )}`
    ].join('');

    window.location.href = mailto;
    setSent(true);
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <section id="contact" className="py-16 bg-white">
      <div className="container mx-auto px-4 md:px-8">
        <motion.h2 
          className="text-3xl font-bold text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.7 }}
          ref={ref}
        >
          Kontakt
        </motion.h2>
        <div className="flex flex-col lg:flex-row gap-12">
          <motion.div 
            className="lg:w-1/2"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="text-xl font-semibold mb-4">Dane kontaktowe</h3>
            <div className="space-y-4">
              {contactItems.map((item, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-start"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                >
                  <motion.div 
                    className="mr-3 text-blue-700"
                    whileHover={{ scale: 1.2, rotate: 5 }}
                  >
                    {item.icon}
                  </motion.div>
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-gray-600">{item.content}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div 
            className="lg:w-1/2"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="text-xl font-semibold mb-4">Napisz do nas</h3>
            <form className="grid grid-cols-2 gap-4" onSubmit={handleSubmit}>
              {formFields.map((field, index) => (
                <motion.div 
                  key={index} 
                  className={field.colSpan}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                >
                  <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  {field.type === "textarea" ? (
                    <textarea
                      id={field.id}
                      rows={field.rows}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form[field.id as keyof typeof form]}
                      onChange={handleChange}
                      required
                    ></textarea>
                  ) : (
                    <input
                      type={field.type}
                      id={field.id}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form[field.id as keyof typeof form]}
                      onChange={handleChange}
                      required
                    />
                  )}
                </motion.div>
              ))}
              <motion.div 
                className="col-span-2 mt-2"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                {error && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4 text-center">
                    {error}
                  </div>
                )}
                {sent && (
                  <div className="bg-green-50 text-green-700 p-3 rounded-md mb-4 text-center">
                    Wiadomość została wysłana!
                  </div>
                )}
                <motion.button 
                  type="submit"
                  className="w-full bg-blue-700 text-white font-bold py-3 px-6 rounded-md hover:bg-blue-800 transition duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                </motion.button>
              </motion.div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};



export default Contact;
